import { createEntityAdapter } from '@reduxjs/toolkit';
import { apiSlice } from '@/slices/apiSlice';

import notification from '../utils/notification';
import { pushJobsToGoogleCalendar } from '@/utils/googleCalendarJobSync';
import {
	prepareWorkflowForBackend,
	normalizeWorkflowForView,
} from '@/utils/normalization';
import { withNotificationsChange } from '@/components/notifications/notificationSettings';

const projectAdapter = createEntityAdapter({
	selectId: (entity) => entity._id,
	// Use _id instead of id
});

const initialState = projectAdapter.getInitialState();

// Scheduled jobs are copied to Google Calendar for locations that turned that on
const pushJobsToGoogleAfter = async (_, { queryFulfilled, getState }) => {
	try {
		await queryFulfilled;
	} catch {
		return;
	}
	pushJobsToGoogleCalendar(getState().auth);
};

export const extendedApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getProjects: builder.query({
			query: () => {
				// Add a timestamp parameter to bypass Firefox caching issues
				// Only for Firefox browser
				const isFirefox = typeof InstallTrigger !== 'undefined';
				if (isFirefox) {
					return `/project/?_t=${Date.now()}`;
				}
				return '/project/';
			},
			transformResponse: (responseData) => {
				// Normalize each project and its nested entities
				const normalizedProjects = responseData.map((project) => {
					return {
						...project,
						runnerInvoice: project.runnerInvoice
							? normalizeWorkflowForView(project.runnerInvoice)
							: null,
						quote: project.quote
							? normalizeWorkflowForView(project.quote)
							: null,
						job: project.job
							? normalizeWorkflowForView(project.job)
							: null,
						opportunity: project.opportunity
							? normalizeWorkflowForView(project.opportunity)
							: null,
					};
				});

				// Use Redux Toolkit's entity adapter to store projects
				return projectAdapter.setAll(initialState, normalizedProjects);
			},
			// providesTags: (result, error, arg) => [
			// 	{ type: 'Project', id: 'LIST' }, // This will invalidate all queries with the tag 'Project'
			// 	...result.ids.map((_id) => ({ type: 'Project', id: _id })), // This provides a tag for each opportunity based on the ID
			// ],
			providesTags: (result) =>
				result
					? [
							{
								type: 'Project',
								id: 'LIST',
							},
							...(result.ids?.map((_id) => {
								return {
									type: 'Project',
									id: _id,
								};
							}) || []),
					  ]
					: [
							{
								type: 'Project',
								id: 'LIST',
							},
					  ],
		}),

		// Fetch a single project by ID (used for refetching updates)
		getProjectById: builder.query({
			query: (id) => `/project/get/${id}`,
			transformResponse: (responseData) => {
				const normalizedData = {
					...responseData,
					runnerInvoice: responseData.runnerInvoice
						? normalizeWorkflowForView(responseData.runnerInvoice)
						: null,
					quote: responseData.quote
						? normalizeWorkflowForView(responseData.quote)
						: null,
					job: responseData.job
						? normalizeWorkflowForView(responseData.job)
						: null,
					opportunity: responseData.opportunity
						? normalizeWorkflowForView(responseData.opportunity)
						: null,
				};
				//  add project to the list of projects
				return projectAdapter.upsertMany(initialState, [
					normalizedData,
				]);
			},
		}),

		// this update function uses the optimistic update pattern and dispatches a refetch of the job after the mutation
		// it does not use the normal invalidatesTags pattern
		// updated job is the full job object with the updated properties, _project is the project id of the job
		// notifications is the form's change, if any; updatedJob's copy is only for display
		quickUpdateJob: builder.mutation({
			query: ({ updatedJob, notifications }) => ({
				url: `/jobs/update/quick`,
				method: 'POST',
				body: {
					updatedJob: withNotificationsChange(
						updatedJob,
						notifications
					),
				},
			}),

			// Trigger refetching of the specific project after updating
			async onQueryStarted(
				{ updatedJob },
				{ dispatch, queryFulfilled, getState }
			) {
				const projectsCache =
					getState().api.queries['getProjects(undefined)']?.data;

				// Get the existing project data from the cache
				const existingProject =
					projectsCache?.entities[updatedJob._project];

				// Merge the existing project data with the update data
				const optimisticProjectData = {
					...existingProject,
					job: {
						...updatedJob,
					},
				};

				// Apply the optimistic update
				const patchResult = dispatch(
					apiSlice.util.updateQueryData(
						'getProjects',
						undefined,
						(draft) => {
							projectAdapter.upsertOne(
								draft,
								optimisticProjectData
							);
						}
					)
				);

				try {
					// Wait for the mutation to complete
					await queryFulfilled;
					pushJobsToGoogleCalendar(getState().auth);

					// Fetch the updated project data, this way we only refetch the project that was updated not all of them
					const response = await dispatch(
						apiSlice.endpoints.getProjectById.initiate(
							updatedJob._project,
							{
								forceRefetch: true, // Ensure a fresh request
							}
						)
					).unwrap();

					// Merge the updated project into the getProjects cache
					dispatch(
						apiSlice.util.updateQueryData(
							'getProjects',
							undefined,
							(draft) => {
								projectAdapter.upsertOne(
									draft,
									response.entities[updatedJob._project]
								);
							}
						)
					);

					// display a notification using the notification redux action
					dispatch(
						notification(
							'Success',
							'Job updated successfully',
							false,
							'job-updated-success-toast'
						)
					);
				} catch (error) {
					// Revert the optimistic update in case of an error
					patchResult.undo();
					console.error('Error updating project:', error);
				}
			},
		}),

		// this update function uses the optimistic update pattern and dispatches a refetch of the job after the mutation
		// it does not use the normal invalidatesTags pattern
		// updated job is the full job object with the updated properties, _project is the project id of the job
		quickUpdateOpportunityBatch: builder.mutation({
			query: ({ updatedOpportunities }) => ({
				url: `/opportunities/update/quick/batch`,
				method: 'POST',
				body: { updatedOpportunities },
			}),
			async onQueryStarted(
				{ updatedOpportunities },
				{ dispatch, queryFulfilled }
			) {
				const patchResult = dispatch(
					apiSlice.util.updateQueryData(
						'getProjects',
						undefined,
						(draft) => {
							updatedOpportunities.forEach(
								(updatedOpportunity) => {
									const project =
										draft.entities[
											updatedOpportunity._project
										];
									if (project) {
										if (
											updatedOpportunity.status ===
											'Closed'
										) {
											project.opportunity = null;
										} else {
											project.opportunity = {
												...project.opportunity,
												...updatedOpportunity,
											};
										}
									}
								}
							);
						}
					)
				);
				try {
					await queryFulfilled;
				} catch {
					patchResult.undo();
				}
			},
		}),

		// this update function uses the optimistic update pattern and dispatches a refetch of the opportunity after the mutation
		// it does not use the normal invalidatesTags pattern
		// updated job is the full job object with the updated properties, _project is the project id of the job
		quickUpdateOpportunity: builder.mutation({
			query: ({ updatedOpportunity }) => ({
				url: `/opportunities/update/quick`,
				method: 'POST',
				body: { updatedOpportunity },
			}),

			// Trigger refetching of the specific project after updating
			async onQueryStarted(
				{ updatedOpportunity },
				{ dispatch, queryFulfilled, getState }
			) {
				const projectsCache =
					getState().api.queries['getProjects(undefined)']?.data;

				// Get the existing project data from the cache
				const existingProject =
					projectsCache?.entities[updatedOpportunity._project];

				// Merge the existing project data with the update data
				const optimisticProjectData = {
					...existingProject,
					opportunity: {
						...updatedOpportunity,
					},
				};

				// Apply the optimistic update
				const patchResult = dispatch(
					apiSlice.util.updateQueryData(
						'getProjects',
						undefined,
						(draft) => {
							projectAdapter.upsertOne(
								draft,
								optimisticProjectData
							);
						}
					)
				);

				try {
					// Wait for the mutation to complete
					await queryFulfilled;

					// Fetch the updated project data, this way we only refetch the project that was updated not all of them
					const response = await dispatch(
						apiSlice.endpoints.getProjectById.initiate(
							updatedOpportunity._project,
							{
								forceRefetch: true, // Ensure a fresh request
							}
						)
					).unwrap();

					// Merge the updated project into the getProjects cache
					dispatch(
						apiSlice.util.updateQueryData(
							'getProjects',
							undefined,
							(draft) => {
								projectAdapter.upsertOne(
									draft,
									response.entities[
										updatedOpportunity._project
									]
								);
							}
						)
					);

					// display a notification using the notification redux action
					dispatch(
						notification(
							'Success',
							'Opportunity updated successfully',
							false,
							'opportunity-updated-success-toast'
						)
					);
				} catch (error) {
					// Revert the optimistic update in case of an error
					patchResult.undo();
					console.error('Error updating opportunity:', error);
				}
			},
		}),

		createProject: builder.mutation({
			query: ({ projectData }) => ({
				url: `/project/create`,
				method: 'POST',
				body: { projectData },
			}),
			transformResponse: (responseData) => {
				// Return data in the entity adapter format expected by components
				return projectAdapter.upsertOne(initialState, responseData);
			},
			async onQueryStarted(_, { dispatch, queryFulfilled }) {
				try {
					// Wait for the mutation to complete
					const { data } = await queryFulfilled;

					// Get the project from the normalized response
					const newProject =
						data.entities[Object.keys(data.entities)[0]];

					// Update the projects cache directly
					dispatch(
						apiSlice.util.updateQueryData(
							'getProjects',
							undefined,
							(draft) => {
								projectAdapter.addOne(draft, newProject);
							}
						)
					);
				} catch (error) {
					console.error('Error creating project:', error);
				}
			},
			// Remove invalidatesTags to prevent refetching
			providesTags: (result) =>
				result ? [{ type: 'Project', id: result.ids[0] }] : [],
		}),

		updateProject: builder.mutation({
			query: ({ projectData }) => ({
				url: `/project/update/${projectData._id}`,
				method: 'POST',
				body: { projectData },
			}),
			transformResponse: (responseData) => {
				//  add project to the list of projects
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			// The project holds the customer shown on its job's Google event
			onQueryStarted: pushJobsToGoogleAfter,
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._id },
			],
		}),

		createOpportunity: builder.mutation({
			query: (opportunityData) => ({
				url: `/opportunities/create`,
				method: 'POST',
				body: { opportunityData },
			}),
			transformResponse: (responseData) => {
				// Add the new opportunity to the list of opportunities
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			providesTags: (result, error, id) => [{ type: 'Project', id }],
			invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
		}),

		updateOpportunity: builder.mutation({
			query: ({ opportunityData }) => ({
				url: `/opportunities/update`,
				method: 'POST',
				body: { opportunityData },
			}),
			transformResponse: (responseData) => {
				//  add project to the list of projects
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			invalidatesTags: (result, error, arg) => {
				return [{ type: 'Project', id: arg._project }];
			},
		}),

		deleteOpportunity: builder.mutation({
			query: ({ _opportunity }) => ({
				url: `/opportunities/delete/${_opportunity}`,
				method: 'DELETE',
			}),
			transformResponse: (responseData) => {
				// Update the project with the opportunity removed
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._project },
			],
		}),

		createQuote: builder.mutation({
			query: (quoteData) => ({
				url: `/quotes/create`,
				method: 'POST',
				body: { quoteData: prepareWorkflowForBackend(quoteData) },
			}),
			transformResponse: (responseData) => {
				// Add the new opportunity to the list of opportunities
				return projectAdapter.upsertMany(initialState, [responseData]);
			},

			invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
		}),

		updateQuote: builder.mutation({
			query: ({ quoteData }) => {
				return {
					url: `/quotes/update`,
					method: 'POST',
					body: { quoteData: prepareWorkflowForBackend(quoteData) },
				};
			},
			transformResponse: (responseData) => {
				//  add project to the list of projects
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._project },
			],
		}),

		deleteQuote: builder.mutation({
			query: ({ _quote }) => ({
				url: `/quotes/delete/${_quote}`,
				method: 'DELETE',
			}),
			transformResponse: (responseData) => {
				// Update the project with the quote removed
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._project },
			],
		}),

		createJob: builder.mutation({
			query: (jobData) => ({
				url: `/jobs/create`,
				method: 'POST',
				body: { jobData: prepareWorkflowForBackend(jobData) },
			}),
			transformResponse: (responseData) => {
				// Add the new opportunity to the list of opportunities
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			onQueryStarted: pushJobsToGoogleAfter,

			invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
		}),

		updateJob: builder.mutation({
			query: ({ jobData }) => {
				return {
					url: `/jobs/update`,
					method: 'POST',
					body: { jobData: prepareWorkflowForBackend(jobData) },
				};
			},
			transformResponse: (responseData) => {
				//  add project to the list of projects
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			onQueryStarted: pushJobsToGoogleAfter,
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._project },
			],
		}),

		deleteJob: builder.mutation({
			query: ({ _job }) => ({
				url: `/jobs/delete/${_job}`,
				method: 'DELETE',
			}),
			transformResponse: (responseData) => {
				// Update the project with the job removed
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			onQueryStarted: pushJobsToGoogleAfter,
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._project },
			],
		}),

		createInvoice: builder.mutation({
			query: (runnerInvoiceData) => ({
				url: `/invoices/create`,
				method: 'POST',
				body: {
					runnerInvoiceData:
						prepareWorkflowForBackend(runnerInvoiceData),
				},
			}),
			transformResponse: (responseData) => {
				// Add the new opportunity to the list of opportunities
				return projectAdapter.upsertMany(initialState, [responseData]);
			},

			invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
		}),

		updateInvoice: builder.mutation({
			query: ({ runnerInvoiceData }) => {
				return {
					url: `/invoices/update`,
					method: 'POST',
					body: {
						runnerInvoiceData:
							prepareWorkflowForBackend(runnerInvoiceData),
					},
				};
			},
			transformResponse: (responseData) => {
				// Add the project to the list of projects
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._project },
			],
		}),

		deleteInvoice: builder.mutation({
			query: ({ _invoice }) => ({
				url: `/invoices/delete/${_invoice}`,
				method: 'DELETE',
			}),
			transformResponse: (responseData) => {
				// Update the project with the invoice removed
				return projectAdapter.upsertMany(initialState, [responseData]);
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Project', id: arg._project },
			],
		}),
	}),
});

export const {
	useGetProjectsQuery,
	useGetProjectByIdQuery,
	useCreateProjectMutation,
	useUpdateProjectMutation,
	useCreateOpportunityMutation,
	useUpdateOpportunityMutation,
	useDeleteOpportunityMutation,
	useCreateQuoteMutation,
	useUpdateQuoteMutation,
	useDeleteQuoteMutation,
	useCreateJobMutation,
	useUpdateJobMutation,
	useDeleteJobMutation,
	useQuickUpdateJobMutation,
	useQuickUpdateOpportunityMutation,
	useQuickUpdateOpportunityBatchMutation,
	useQuickUpdateJobBatchMutation,
	useUpdateAssigneesMutation,
	useCreateInvoiceMutation,
	useUpdateInvoiceMutation,
	useDeleteInvoiceMutation,
} = extendedApiSlice;

export const { util } = extendedApiSlice;
